import tv, { SchemaType } from "typed-object-validator";
import { GlobalClientSettings, GlobalSettings, GlobalSettingsSchema } from "./Admin";
import { EventReservation } from "./Event";
import { Language, LanguageSchema } from "./Language";
import { RegionToTest, Test, PartialTestSchema } from "./Test";
import { User } from "./User";
import { Event, PartialEventSchema } from "./Event";
import { Questionnaire } from "./Questionnaire";
import { Registration, RegistrationStatus, RegistrationStatusSchema } from "./Registration";
import { PartialQuestionnaireSchema } from ".";
import { Region } from "./Region";

export const UserAccountCheckRequestSchema = tv.object({
    token: tv.string().min(10),
});
export type UserAccountCheckRequest = SchemaType<typeof UserAccountCheckRequestSchema>;

export type UserAccountCheckResponse = {
    telephoneNumber: string;
    language: Language;
    regionName: string;
};

export const UserAccountCheckUpdateRequestSchema = tv.object({
    token: tv.string().min(10),
    telephoneNumber: tv
        .string("error-register-required-telephone")
        .regex(/^\+?[0-9\s.()]{7,28}$/, "error-register-invalid-telephone")
        .min(1, "error-register-required-telephone"),
    language: LanguageSchema,
    regionName: tv.string("regions-select").min(1, "regions-select").max(30),
});
export type UserAccountCheckUpdateRequest = SchemaType<typeof UserAccountCheckUpdateRequestSchema>;

export type UserFriendInvitesResponse = {
    users: User[];
};

export type Endpoints = {
    "/user/passwordreset/finish": [UserPasswordResetFinishRequest, UserPasswordResetFinishResponse];
    "/user/passwordreset": [UserPasswordResetRequest, UserPasswordResetResponse];
    "/user/enablepassword": [UserEnablePasswordRequest, UserEnablePasswordResponse];
    "/user/register": [UserRegisterRequest, UserRegisterResponse];
    "/user/register/dummy": [any, any];
    "/user/token": [UserTokenRequest, UserTokenResponse];
    "/user/login": [UserLoginRequest, UserLoginResponse];
    "/user/registrations": [never, UserRegistrationsResponse];
    "/user/update": [UserUpdateRequest, UserUpdateResponse];
    "/user/delete": [UserDeleteRequest, UserDeleteResponse];
    "/user/updateregion": [UserUpdateRegionRequest, UserUpdateRegionResponse];
    "/user/emailpreference": [UserEmailPreferenceRequest, UserEmailPreferenceResponse];
    "/user/emailpreference/update": [UserEmailPreferenceUpdateRequest, never];
    "/user/update-password": [UserUpdatePasswordRequest, UserUpdatePasswordResponse];
    "/user/account-checkup": [UserAccountCheckRequest, UserAccountCheckResponse];
    "/user/account-checkup/update": [UserAccountCheckUpdateRequest, never];
    "/user/friendinvites": [never, UserFriendInvitesResponse];

    "/test/notify": [TestNotifyRequest, never];
    "/test/events": [TestEventsRequest, TestEventsResponse];
    "/test/create": [TestCreateRequest, TestCreateResponse];
    "/test/update": [TestUpdateRequest, TestUpdateResponse];
    "/test/apply": [TestApplyRequest, TestApplyResponse];
    "/test/delete": [TestDeleteRequest, TestDeleteResponse];
    "/test/registrations": [TestRegistrationsRequest, TestRegistrationsResponse];
    "/test/registration": [TestRegistrationRequest, TestRegistrationResponse];
    "/test/full": [TestFullRequest, TestFullResponse];
    "/test/registration/cancel": [TestRegistrationCancelRequest, never];
    "/test": [TestRequest, TestResponse];
    "/test/registration/delete": [TestRegistrationDeleteRequest, never];
    "/test/registration/update": [TestRegistrationUpdateRequest, TestRegistrationUpdateResponse];
    "/test/registrations/mark": [TestRegistrationsMarkRequest, TestRegistrationsMarkResponse];

    "/region/create": [RegionCreateRequest, RegionCreateResponse];
    "/region/delete": [RegionDeleteRequest, never];
    "/region/update": [RegionUpdateRequest, RegionUpdateResponse];
    "/region/list/public": [never, RegionListPublicResponse];
    "/region/list": [never, RegionListResponse];
    "/region/tests/public": [never, RegionTestsPublicResponse];
    "/region/tests": [RegionTestsRequest, RegionTestsResponse];

    "/questionnaire/notify": [QuestionnaireNotifyRequest, never];
    "/questionnaire/delete": [QuestionnaireDeleteRequest, never];
    "/questionnaire/create": [QuestionnaireCreateRequest, QuestionnaireCreateResponse];
    "/questionnaire/update": [QuestionnaireUpdateRequest, QuestionnaireUpdateResponse];
    "/questionnaire/answer": [QuestionnaireAnswerRequest, never];
    "/questionnaire/answer/finish": [QuestionnaireAnswerFinishRequest, QuestionnaireAnswerFinishResponse];
    "/questionnaire/answers": [QuestionnaireAnswersRequest, QuestionnaireAnswersResponse];
    "/questionnaire/questions": [QuestionnaireQuestionsRequest, QuestionnaireQuestionsResponse];
    "/questionnaire": [QuestionnaireRequest, QuestionnaireResponse];

    "/event/list": [never, EventListResponse];
    "/event/cancelreserve": [EventCancelReserveRequest, never];
    "/event/reserve": [EventReserveRequest, EventReserveResponse];
    "/event/present": [EventPresentRequest, never];
    "/event/reservations": [EventReservationsRequest, EventReservationsResponse];
    "/event/delete": [EventDeleteRequest, never];
    "/event/create": [EventCreateRequest, EventCreateResponse];
    "/event/notify": [EventNotifyRequest, never];
    "/event/update": [EventUpdateRequest, EventUpdateResponse];
    "/event": [EventRequest, EventResponse];

    "/admin/tests": [AdminTestsRequest, AdminTestsResponse];
    "/admin/users": [AdminUsersRequest, AdminUsersResponse];
    "/admin/users/markfriendinvite": [AdminUsersMarkFriendInviteRequest, never];
    "/admin/settings": [never, AdminSettingsResponse];
    "/admin/settings/update": [AdminSettingsUpdateRequest, never];
    "/admin/mailer": [AdminMailerRequest, AdminMailerResponse];
    "/admin/notify/advanced": [AdminNotifyAdvancedRequest, AdminNotifyAdvancedResponse];
    "/admin/user/get": [AdminGetUserRequest, AdminGetUserResponse];
    "/admin/user/update": [AdminUpdateUserRequest, AdminUpdateUserResponse];

    "/status": [never, StatusResponse];
    "/asked": [never, AskedResponse];

    "/file/upload": [FileUploadRequest, FileUploadResponse];
};

export type AskedQuestion = {
    id: number;
    category: string;
    language: Language;
    question: string;
    response: string;
};

export type AskedResponse = AskedQuestion[];

export const FileUploadRequestSchema = tv.object({
    name: tv.string(),
    data: tv.string(),
});
export type FileUploadRequest = SchemaType<typeof FileUploadRequestSchema>;

export interface FileUploadResponse {
    url: string;
}

/* ================ USER ROUTES ================ */

/* /user/email-preference */
export const UserEmailPreferenceRequestSchema = tv.object({
    token: tv.string(),
});
export type UserEmailPreferenceRequest = SchemaType<typeof UserEmailPreferenceRequestSchema>;
export interface UserEmailPreferenceResponse {
    enableEmail: boolean;
    enableNotificationEmail: boolean;
}

/* /user/email-preference/update */
export const UserEmailPreferenceUpdateRequestSchema = tv.object({
    token: tv.string(),
    enableEmail: tv.boolean(),
    enableNotificationEmail: tv.boolean(),
});
export type UserEmailPreferenceUpdateRequest = SchemaType<typeof UserEmailPreferenceUpdateRequestSchema>;

/* /user/token */
export const UserTokenRequestSchema = tv.object({
    refreshToken: tv.string(),
});
export type UserTokenRequest = SchemaType<typeof UserTokenRequestSchema>;
export interface UserTokenResponse {
    accessToken: string;
    refreshToken: string;
    user: User;
}

/* /user/enable-password */
export const UserEnablePasswordRequestSchema = tv.object({
    email: tv.email("error-login-invalid-email").min(3, "error-login-required-email").max(100, "error-login-invalid-email"),
    password: tv.string().min(1, "error-login-required-password").max(40, "error-login-long-password"),
});
export type UserEnablePasswordRequest = SchemaType<typeof UserEnablePasswordRequestSchema>;
export type UserEnablePasswordResponse =
    | {
          status: "ok";
          accessToken: string;
          refreshToken: string;
          user: User;
      }
    | {
          status: "error";
      };

/* /user/login */
export const UserLoginRequestSchema = tv.object({
    email: tv.email("error-login-invalid-email").min(3, "error-login-required-email").max(100, "error-login-invalid-email"),
    password: tv.string().max(40, "error-login-long-password"), // .min(1, "error-login-required-password")
});
export type UserLoginRequest = SchemaType<typeof UserLoginRequestSchema>;
export type UserLoginResponse =
    | { status: "unset" }
    | {
          status: "error";
          email?: string;
          password?: string;
      }
    | {
          status: "ok";
          accessToken: string;
          refreshToken: string;
          user: User;
      };

/* /user/register */
export const UserRegisterRequestSchema = tv.object({
    firstName: tv.string("error-register-required-firstname").min(2, "error-register-required-firstname").max(30, "error-register-long-firstname"),
    lastName: tv.string("error-register-required-lastname").min(2, "error-register-required-lastname").max(30, "error-register-long-lastname"),
    email: tv
        .email("error-register-invalid-email", "error-register-required-email")
        .min(2, "error-register-required-email")
        .max(60, "error-register-invalid-email"),
    birthDate: tv.number("error-register-required-birthdate"),
    iban: tv.string("error-register-required-iban").min(8, "error-register-required-iban").max(100, "error-register-invalid-iban"),
    gender: tv.value("male").or(tv.value("female", "error-register-required-gender")),
    password: tv.string("error-register-required-password").min(7, "error-register-short-password").max(40, "error-register-long-password"),
    howFound: tv.string("error-register-required-howfound").min(1, "error-register-required-howfound").max(100, "error-register-long-howfound"),
    telephoneNumber: tv
        .string("error-register-required-telephone")
        .regex(/^\+?[0-9\s.()]{7,28}$/, "error-register-invalid-telephone")
        .min(1, "error-register-required-telephone"),
    registerFriendId: tv.number().nullable(),
    language: LanguageSchema,
});
export type UserRegisterRequest = SchemaType<typeof UserRegisterRequestSchema>;
export type UserRegisterResponse =
    | { status: "unset" }
    | {
          status: "error";
          email?: string;
          iban?: string;
      }
    | {
          status: "ok";
          accessToken: string;
          refreshToken: string;
          user: User;
      };

/* /user/update-password */
export const UserUpdatePasswordRequestSchema = tv.object({
    newPassword: tv.string().min(7, "error-change-password-short-password").max(40, "error-change-password-long-password"),
    oldPassword: tv.string().min(7, "error-change-password-short-password").max(40, "error-change-password-long-password"),
});
export type UserUpdatePasswordRequest = SchemaType<typeof UserUpdatePasswordRequestSchema>;
export type UserUpdatePasswordResponse =
    | {
          status: "error";
          oldPassword?: string;
          newPassword?: string;
      }
    | { status: "ok" };

/* /user/update */
export const UserUpdateRequestSchema = tv.object({
    iban: tv.string().min(8, "error-register-required-iban").max(100, "error-register-invalid-iban"),
    email: tv.email("error-register-invalid-email").min(1, "error-register-required-email").max(100, "error-register-invalid-email"),
    birthTimeStamp: tv.number(),
    telephoneNumber: tv
        .string()
        .regex(/^\+?[0-9\s.()]{7,28}$/, "error-register-invalid-telephone")
        .min(1, "error-register-required-telephone")
        .optional(),
    language: LanguageSchema,
    enableEmail: tv.boolean(),
    enableNotificationEmail: tv.boolean(),
});
export type UserUpdateRequest = SchemaType<typeof UserUpdateRequestSchema>;
export type UserUpdateResponse =
    | {
          status: "error";
          email?: string;
          iban?: string;
      }
    | {
          status: "ok";
          user: User;
      };

/* /user/reset-password/finish */
export const UserPasswordResetFinishRequestSchema = tv.object({
    token: tv.string().min(20).max(400),
    newPassword: tv.string().min(7, "error-register-short-password").max(40, "error-register-long-password"),
});
export type UserPasswordResetFinishRequest = SchemaType<typeof UserPasswordResetFinishRequestSchema>;
export type UserPasswordResetFinishResponse =
    | {
          status: "ok";
          accessToken: string;
          refreshToken: string;
          user: User;
      }
    | {
          status: "error";
          token: string;
      };

/* /user/reset-password */
export const UserPasswordResetRequestSchema = tv.object({
    email: tv.email("error-register-invalid-email").min(1, "error-register-required-email").max(100, "error-register-invalid-email"),
});
export type UserPasswordResetRequest = SchemaType<typeof UserPasswordResetRequestSchema>;
export type UserPasswordResetResponse =
    | {
          status: "error";
          email?: string;
      }
    | { status: "ok" };

/* /user/registrations */
export interface UserRegistrationsResponse {
    registrations: Registration[];
}

/* /user/update-region */
export const UserUpdateRegionRequestSchema = tv.object({
    activeRegionName: tv.string(),
});
export type UserUpdateRegionRequest = SchemaType<typeof UserUpdateRegionRequestSchema>;
export interface UserUpdateRegionResponse {
    user: User;
}

/* /user/delete */
export const UserDeleteRequestSchema = tv.object({
    password: tv.string().min(7, "error-register-short-password").max(40, "error-register-long-password"),
});
export type UserDeleteRequest = SchemaType<typeof UserDeleteRequestSchema>;
export type UserDeleteResponse =
    | {
          status: "error";
          password?: string;
      }
    | { status: "ok" };

/* ================ TEST ROUTES ================ */

/* /test/notify */
export const TestNotifyRequestSchema = tv.object({
    id: tv.number(),
});
export type TestNotifyRequest = SchemaType<typeof TestNotifyRequestSchema>;

/* /test/create */
export const TestCreateRequestSchema = tv.object({
    name: tv.string().min(1, "Geef je onderzoek een naam").max(100, "Deze naam is te lang. Gebruik de beschrijving voor een langere tekst."),
    language: LanguageSchema,
    regionName: tv.string().optional(),
});
export type TestCreateRequest = SchemaType<typeof TestCreateRequestSchema>;
export interface TestCreateResponse {
    test: Test;
}

/* /test/update */
export const TestUpdateRequestSchema = tv.object({
    test: PartialTestSchema,
    notifyUsers: tv.boolean().optional(),
});
export type TestUpdateRequest = SchemaType<typeof TestUpdateRequestSchema>;
export type TestUpdateResponse = {
    status: "ok";
    test: Test;
};

/* /test/delete */
export const TestDeleteRequestSchema = tv.object({
    testId: tv.number(),
});
export type TestDeleteRequest = SchemaType<typeof TestDeleteRequestSchema>;
export interface TestDeleteResponse {
    error?: string;
}

/* /test */
export const TestRequestSchema = tv.object({
    testId: tv.number(),
    excludeAnswered: tv.boolean().optional(),
    registrationQuestionsOnly: tv.boolean().optional(),
});
export type TestRequest = SchemaType<typeof TestRequestSchema>;
export interface TestResponse {
    test: Test;
}

/* /test/full */
export const TestFullRequestSchema = tv.object({
    testId: tv.number(),
});
export type TestFullRequest = SchemaType<typeof TestFullRequestSchema>;
export type TestFullResponse = TestResponse;

/* /test/apply */
export const TestApplyRequestSchema = tv.object({
    testId: tv.number(),
});
export type TestApplyRequest = SchemaType<typeof TestApplyRequestSchema>;
export type TestApplyResponse =
    | {
          status: "error";
          error: string;
      }
    | {
          status: "ok";
          satisfies: boolean;
      };

/* /test/registrations */
export const TestRegistrationsRequestSchema = tv.object({
    testId: tv.number(),
});
export type TestRegistrationsRequest = SchemaType<typeof TestRegistrationsRequestSchema>;
export interface TestRegistrationsResponse {
    registrations: Registration[];
}

/* /test/registration */
export const TestRegistrationRequestSchema = tv.object({
    userId: tv.number(),
    testId: tv.number(),
});
export type TestRegistrationRequest = SchemaType<typeof TestRegistrationRequestSchema>;
export interface TestRegistrationResponse {
    registration: Registration;
}

/* /test/registration/update */
export const TestRegistrationUpdateRequestSchema = tv.object({
    testId: tv.number(),
    userId: tv.number(),
    revalidateConditions: tv.boolean().optional(),
    newStatus: RegistrationStatusSchema.optional(),
});
export type TestRegistrationUpdateRequest = SchemaType<typeof TestRegistrationUpdateRequestSchema>;
export interface TestRegistrationUpdateResponse {
    registration: Registration;
}

/* /test/registration/delete */
export const TestRegistrationDeleteRequestSchema = tv.object({
    userId: tv.number(),
    testId: tv.number(),
});
export type TestRegistrationDeleteRequest = SchemaType<typeof TestRegistrationDeleteRequestSchema>;

/* /test/events */
export const TestEventsRequestSchema = tv.object({
    testId: tv.number(),
});
export type TestEventsRequest = SchemaType<typeof TestEventsRequestSchema>;
export interface TestEventsResponse {
    events: Event[];
}

/* /test/registrations/mark */
export const TestRegistrationsMarkRequestSchema = tv.object({
    testId: tv.number(),
    mark: tv.object(), // key: RegistrationStatus, value: number[]
});
export type TestRegistrationsMarkRequest = SchemaType<typeof TestRegistrationsMarkRequestSchema>;
export interface TestRegistrationsMarkResponse {
    registrations: Registration[];
}

/* /test/registration/cancel */
export const TestRegistrationCancelRequestSchema = tv.object({
    testId: tv.number(),
});
export type TestRegistrationCancelRequest = SchemaType<typeof TestRegistrationCancelRequestSchema>;

/* ================ REGION ROUTES ================ */

export const PartialRegionSchema = tv.object({
    name: tv.string().min(1).max(30),
    open: tv.boolean(),
});
export type PartialRegion = SchemaType<typeof PartialRegionSchema>;

/* /region/list */
export interface RegionListResponse {
    regions: Region[];
}

/* /region/list/public */
export interface RegionListPublicResponse {
    regions: Region[];
}

/* /region/tests */
export const RegionTestsRequestSchema = tv.object({
    regionName: tv.string().min(1).max(30),
});
export type RegionTestsRequest = SchemaType<typeof RegionTestsRequestSchema>;
export interface RegionTestsResponse {
    region: Region;
}

/* /region/tests/public */
export interface RegionTestsPublicResponse {
    region: Region;
}

/* /region/create */
export const RegionCreateRequestSchema = tv.object({
    regionName: tv.string().min(1).max(30),
});
export type RegionCreateRequest = SchemaType<typeof RegionCreateRequestSchema>;
export interface RegionCreateResponse {
    region: PartialRegion;
}

/* /region/delete */
export const RegionDeleteRequestSchema = tv.object({
    regionName: tv.string(),
});
export type RegionDeleteRequest = SchemaType<typeof RegionDeleteRequestSchema>;

/* /region/update */
export const RegionUpdateRequestSchema = tv.object({
    name: tv.string().min(1).max(30),
    open: tv.boolean(),
});
export type RegionUpdateRequest = SchemaType<typeof RegionUpdateRequestSchema>;
export interface RegionUpdateResponse {
    region: PartialRegion;
}

/* ================ QUESTIONNAIRE ROUTES ================ */

/* /questionnaire/answer/finish */
export const QuestionnaireAnswerFinishRequestSchema = tv.object({
    id: tv.string(),
});
export type QuestionnaireAnswerFinishRequest = SchemaType<typeof QuestionnaireAnswerFinishRequestSchema>;
export type QuestionnaireAnswerFinishResponse = { status: "ok" } | { status: "error"; error: string };

/* /questionnaire/answer */
export const QuestionnaireAnswerRequestSchema = tv.object({
    questionId: tv.number(),
    value: tv.object(),
});
export type QuestionnaireAnswerRequest = SchemaType<typeof QuestionnaireAnswerRequestSchema>;

/* /questionnaire/notify */
export const QuestionnaireNotifyRequestSchema = tv.object({
    id: tv.string(),
});
export type QuestionnaireNotifyRequest = SchemaType<typeof QuestionnaireNotifyRequestSchema>;

/* /questionnaire/questions */
export const QuestionnaireQuestionsRequestSchema = tv.object({
    id: tv.string(),
    excludeAnswered: tv.boolean().optional(),
});
export type QuestionnaireQuestionsRequest = SchemaType<typeof QuestionnaireQuestionsRequestSchema>;
export type QuestionnaireQuestionsResponse =
    | {
          status: "ok";
          questionnaire: Questionnaire;
      }
    | { status: "error"; error: "not-found" }
    | { status: "error"; error: "test-not-registered"; testId: number };

/* /questionnaire/answers */
export const QuestionnaireAnswersRequestSchema = tv.object({
    userId: tv.number(),
    id: tv.string(),
});
export type QuestionnaireAnswersRequest = SchemaType<typeof QuestionnaireAnswersRequestSchema>;
export interface QuestionnaireAnswersResponse {
    user: User;
}

/* /questionnaire */
export const QuestionnaireRequestSchema = tv.object({
    id: tv.string(),
});
export type QuestionnaireRequest = SchemaType<typeof QuestionnaireRequestSchema>;
export interface QuestionnaireResponse {
    questionnaire: Questionnaire;
    users: User[];
}

/* /questionnaire/create */
export const QuestionnaireCreateRequestSchema = tv.object({
    name: tv.string(),
    testId: tv.number().optional(),
});
export type QuestionnaireCreateRequest = SchemaType<typeof QuestionnaireCreateRequestSchema>;
export interface QuestionnaireCreateResponse {
    questionnaire: Questionnaire;
}

/* /questionnaire/update */
export const QuestionnaireUpdateRequestSchema = tv.object({
    questionnaire: PartialQuestionnaireSchema,
    notifyUsers: tv.boolean().optional(),
});
export type QuestionnaireUpdateRequest = SchemaType<typeof QuestionnaireUpdateRequestSchema>;
export interface QuestionnaireUpdateResponse {
    questionnaire: Questionnaire;
}

/* /questionnaire/delete */
export const QuestionnaireDeleteRequestSchema = tv.object({
    questionnaireId: tv.string(),
});
export type QuestionnaireDeleteRequest = SchemaType<typeof QuestionnaireDeleteRequestSchema>;

/* ================ EVENT ROUTES ================ */

/* /event/notify */
export const EventNotifyRequestSchema = tv.object({
    id: tv.string(),
});
export type EventNotifyRequest = SchemaType<typeof EventNotifyRequestSchema>;

/* /event/reserve */
export const EventReserveRequestSchema = tv.object({
    locationId: tv.number(),
    time: tv.number(),
});
export type EventReserveRequest = SchemaType<typeof EventReserveRequestSchema>;
export interface EventReserveResponse {
    error: string;
}

/* /event/update */
export const EventUpdateRequestSchema = tv.object({
    event: PartialEventSchema,
    notifyUsers: tv.boolean().optional(),
});
export type EventUpdateRequest = SchemaType<typeof EventUpdateRequestSchema>;
export interface EventUpdateResponse {
    event: Event;
}

/* /event */
export const EventRequestSchema = tv.object({
    id: tv.string(),
});
export type EventRequest = SchemaType<typeof EventRequestSchema>;
export interface EventResponse {
    event: Event;
}

/* /event/list */
export interface EventListResponse {
    events: Event[];
}

/* /event/create */
export const EventCreateRequestSchema = tv.object({
    title: tv.string().min(1, "Geef je event een langere naam.").max(100, "Te lang."),
    description: tv.string().max(1000, "Te lang."),
    testId: tv.number().optional(),
});
export type EventCreateRequest = SchemaType<typeof EventCreateRequestSchema>;
export interface EventCreateResponse {
    event: Event;
}

/* /event/delete */
export const EventDeleteRequestSchema = tv.object({
    eventId: tv.string(),
});
export type EventDeleteRequest = SchemaType<typeof EventDeleteRequestSchema>;

/* /event/reservations */
export const EventReservationsRequestSchema = tv.object({
    id: tv.string(),
});
export type EventReservationsRequest = SchemaType<typeof EventReservationsRequestSchema>;
export interface EventReservationsResponse {
    reservations: EventReservation[];
}

/* /event/present */
export const EventPresentRequestSchema = tv.object({
    present: tv.boolean(),
    userId: tv.number(),
    eventId: tv.string(),
});
export type EventPresentRequest = SchemaType<typeof EventPresentRequestSchema>;

/* /event/cancelreserve */
export const EventCancelReserveRequestSchema = tv.object({
    userId: tv.number(),
    eventId: tv.string(),
});
export type EventCancelReserveRequest = SchemaType<typeof EventCancelReserveRequestSchema>;

/* ================ ADMIN ROUTES ================ */

/* /admin/user/get */
export const AdminGetUserRequestSchema = tv.object({
    userId: tv.number(),
});
export type AdminGetUserRequest = SchemaType<typeof AdminGetUserRequestSchema>;
export type AdminGetUserResponse = {
    user: User;
};

/* /admin/user/update */
export const AdminUpdateUserRequestSchema = tv.object({
    id: tv.number(),
    activeRegionName: tv.string(),
    firstName: tv.string("error-register-required-firstname").min(2, "error-register-required-firstname").max(30, "error-register-long-firstname"),
    lastName: tv.string("error-register-required-lastname").min(2, "error-register-required-lastname").max(30, "error-register-long-lastname"),
    iban: tv.string().min(8, "error-register-required-iban").max(100, "error-register-invalid-iban"),
    email: tv.email("error-register-invalid-email").min(1, "error-register-required-email").max(100, "error-register-invalid-email"),
    birthTimeStamp: tv.number(),
    gender: tv.value("male").or(tv.value("female")),
    telephoneNumber: tv.string().doSetWhenEmpty(null).nullable(),
    language: LanguageSchema,
    enableEmail: tv.boolean(),
    enableNotificationEmail: tv.boolean(),
    password: tv.string().doSetWhenEmpty(undefined),
    howFound: tv.string(),
});
export type AdminUpdateUserRequest = SchemaType<typeof AdminUpdateUserRequestSchema>;
export type AdminUpdateUserResponse = {
    user: User;
};

export interface AdminTestsRequest {
    includeArchived?: boolean;
}

/* /admin/tests */
export interface AdminTestsResponse {
    tests: Test[];
}

/* /admin/users */
export const AdminUsersRequestSchema = tv.object({
    page: tv.number(),
    usersPerPage: tv.number().optional(),
    query: tv.string().optional(),
    invitedOnly: tv.boolean().optional(),
    password: tv.string(),
});
export type AdminUsersRequest = SchemaType<typeof AdminUsersRequestSchema>;
export interface AdminUsersResponse {
    users: User[];
    userCount: number;
    invitedUserCount: number;
}

/* /admin/users/markfriendinvite */
export const AdminUsersMarkFriendInviteRequestSchema = tv.object({
    mark: tv.object({
        rewarded: tv.array(tv.number()).optional(),
        notRewarded: tv.array(tv.number()).optional(),
    }),
});
export type AdminUsersMarkFriendInviteRequest = SchemaType<typeof AdminUsersMarkFriendInviteRequestSchema>;

/* /admin/settings/update */
export const AdminSettingsUpdateRequestSchema = tv.object({
    settings: GlobalSettingsSchema,
});
export type AdminSettingsUpdateRequest = SchemaType<typeof AdminSettingsUpdateRequestSchema>;

/* /admin/settings */
export interface AdminSettingsResponse {
    settings: GlobalSettings;
}

/* /admin/mailer/advanced */
export const AdminNotifyAdvancedRequestSchema = tv.object({
    testMode: tv.boolean(),
    subject: tv.string().min(5, "Geef een langer onderwerp in.").max(100, "Je onderwerp is te lang."),
    template: tv.string().min(30, "Geef een langer bericht in.").max(2000, "Te lang."),
    mode: tv.value("sms").or(tv.value("email")),
    receivers: tv
        .array(
            tv.object({
                destination: tv.string(),
                data: tv.object(),
            })
        )
        .min(1, "Upload een gebruikerslijst."),
});
export type AdminNotifyAdvancedRequest = SchemaType<typeof AdminNotifyAdvancedRequestSchema>;
export type AdminNotifyAdvancedResponse =
    | {
          status: "ok";
          userCount: number;
      }
    | {
          status: "error";
          message: string;
      };

/* /admin/mailer */
export const AdminMailerRequestSchema = tv.object({
    targetLanguage: LanguageSchema,
    inTestId: tv.number().optional(),
    subject: tv.string().min(5, "Geef een langer onderwerp in.").max(100, "Je onderwerp is te lang."),
    content: tv.string().min(50, "Geef een langer bericht in.").max(2000, "Te lang."),
});
export type AdminMailerRequest = SchemaType<typeof AdminMailerRequestSchema>;
export interface AdminMailerResponse {
    userCount: number;
}

/* /status */
export interface StatusResponse {
    settings: GlobalClientSettings;
}
